import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PinGlyph from "../../../common/PinGlyph";
import usePropertyPins from "../../../../hooks/usePropertyPins";
import { PIN_STATUSES, pinPlacers } from "../../../../helpers/propertyPins";

/**
 * URL / search keys for My pins: the backend reads the statuses as comma lists
 * and `pins_placed_by` as one member id (only that person's pins; on its own,
 * all of them).
 */
export const PIN_FILTER_KEYS = ["pins_includes_arr", "pins_excludes_arr", "pins_placed_by"];

/**
 * My pins: Include or Exclude properties by the company's job tracker status,
 * like the labels table in My properties, and "Placed by" to keep to one
 * person's pins. Every seat on the account sees the same pins, so the counts
 * and the results are the company's (or that person's).
 */
export default function PinsOptions({ setValue, watch }) {
  const location = useLocation();
  const { pins, viewerId, ownerId } = usePropertyPins();

  // Restore a filter that came in on the URL (refresh, saved search, shared link).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    ["pins_includes_arr", "pins_excludes_arr"].forEach((key) => {
      const raw = params.get(key);
      if (raw) setValue(key, raw.split(",").filter(Boolean));
    });
    const placed = params.get("pins_placed_by");
    if (placed) setValue("pins_placed_by", placed);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const includes = watch("pins_includes_arr") || [];
  const excludes = watch("pins_excludes_arr") || [];
  const placedBy = watch("pins_placed_by") || "";

  // Who placed the company's pins, as on the map's "Placed by".
  const placers = useMemo(() => pinPlacers(pins, { viewerId, ownerId }), [pins, viewerId, ownerId]);

  // The counts are the chosen person's when one is chosen.
  const counts = useMemo(() => {
    const byStatus = {};
    (pins || [])
      .filter((pin) => !placedBy || String(pin.created_by?.id) === placedBy)
      .forEach((pin) => {
        byStatus[pin.status] = (byStatus[pin.status] || 0) + 1;
      });
    return byStatus;
  }, [pins, placedBy]);

  // A status is either included or excluded, never both.
  const toggle = (key, field) => {
    const own = field === "pins_includes_arr" ? includes : excludes;
    const otherField = field === "pins_includes_arr" ? "pins_excludes_arr" : "pins_includes_arr";
    const other = field === "pins_includes_arr" ? excludes : includes;

    setValue(field, own.includes(key) ? own.filter((value) => value !== key) : [...own, key]);
    if (other.includes(key)) {
      setValue(otherField, other.filter((value) => value !== key));
    }
  };

  return (
    <>
      <div className="form_blk margin-top-neg">
        <h6 className="f_s_14 f_dim_light">Job tracker pins</h6>
      </div>
      <div className="form_blk margin-top-neg lbl_table_search" data-testid="pins-filter">
        <table>
          <thead>
            <tr>
              <th>Pins</th>
              <th>Includes</th>
              <th>Exclude</th>
            </tr>
          </thead>
          <tbody>
            {PIN_STATUSES.map((status) => (
              <tr key={status.key}>
                <td>
                  <span style={styles.status}>
                    <PinGlyph color={status.color} height={14} />
                    {status.label} ({counts[status.key] || 0})
                  </span>
                </td>
                <td>
                  <input
                    type="checkbox"
                    name="pins_includes_arr"
                    aria-label={`Include ${status.label}`}
                    checked={includes.includes(status.key)}
                    onChange={() => toggle(status.key, "pins_includes_arr")}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    name="pins_excludes_arr"
                    aria-label={`Exclude ${status.label}`}
                    checked={excludes.includes(status.key)}
                    onChange={() => toggle(status.key, "pins_excludes_arr")}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {placers.length > 0 && (
        <div className="form_blk margin-top-neg">
          <h6 className="f_s_14 f_dim_light">Placed by</h6>
          <select
            className="input"
            aria-label="Placed by"
            value={placedBy}
            onChange={(event) => setValue("pins_placed_by", event.target.value)}
          >
            <option value="">Everyone</option>
            {placers.map((person) => (
              <option key={person.id} value={String(person.id)}>
                {person.label} ({person.count})
              </option>
            ))}
          </select>
          {placedBy && includes.length === 0 && (
            <p className="f_s_14 f_dim_light" style={styles.hint}>
              No status ticked: shows every property this person pinned.
            </p>
          )}
        </div>
      )}
    </>
  );
}

const styles = {
  status: { display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
  hint: { margin: "6px 0 0", fontSize: 12, lineHeight: 1.4 },
};
